/**
 * Helper functions for sorting and grouping work queue tasks
 */

// Helper function to get priority weight for sorting
const getPriorityWeight = (priority) => {
	const noPriorityWeight = 999;
	const priorityMap = {
		High: 0,
		Normal: 1,
		Low: 2,
	};
	if (priority === null || priority === undefined || priority === '') {
		return noPriorityWeight;
	}
	const weight = priorityMap[priority];
	return weight !== undefined ? weight : noPriorityWeight; // Unrecognized priorities treated as no priority
};

// Group tasks by priority, sorted by due date ascending within each group
export const groupTasksByPriority = (tasks) => {
	const groups = {
		High: [],
		Normal: [],
		Low: [],
		NoPriority: [],
	};

	tasks.forEach((task) => {
		const priority = task.priority;
		if (priority === 'High' || priority === 'Normal' || priority === 'Low') {
			groups[priority].push(task);
		} else {
			// Handle unrecognized priority values
			groups.NoPriority.push(task);
		}
	});

	// Sort each group by due date ascending (most urgent first)
	const sortGroupByDueDate = (group) => {
		return group.sort((a, b) => {
			if (!a.due_date && !b.due_date) return 0;
			if (!a.due_date) return 1;
			if (!b.due_date) return -1;
			return new Date(a.due_date) - new Date(b.due_date);
		});
	};

	const sections = [];

	// Organize groups into 'sections' with headings
	if (groups.High.length > 0) {
		sections.push({ section: 'High', items: sortGroupByDueDate(groups.High) });
	}
	if (groups.Normal.length > 0) {
		sections.push({
			section: 'Normal',
			items: sortGroupByDueDate(groups.Normal),
		});
	}
	if (groups.Low.length > 0) {
		sections.push({ section: 'Low', items: sortGroupByDueDate(groups.Low) });
	}

	if (groups.NoPriority.length > 0) {
		sections.push({
			section: 'No Priority',
			items: sortGroupByDueDate(groups.NoPriority),
		});
	}

	return sections;
};

// Group tasks by due date, sorted by priority within each group
export const groupTasksByDueDate = (tasks) => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const endOfWeek = new Date(today);
	endOfWeek.setDate(today.getDate() + (7 - today.getDay()));

	const overdue = [];
	const dueThisWeek = [];
	const upcoming = [];
	const noDueDate = [];

	tasks.forEach((task) => {
		if (!task.due_date) {
			noDueDate.push(task);
			return;
		}

		const dueDate = new Date(task.due_date + 'T00:00:00');
		if (dueDate < today) {
			overdue.push(task);
		} else if (dueDate <= endOfWeek) {
			dueThisWeek.push(task);
		} else {
			upcoming.push(task);
		}
	});

	// Sort each group by priority
	const sortGroupByPriority = (group) => {
		return group.sort((a, b) => {
			return getPriorityWeight(a.priority) - getPriorityWeight(b.priority);
		});
	};

	const sections = [];
	// Organize groups into 'sections' with headings
	if (overdue.length > 0) {
		sections.push({ section: 'Overdue', items: sortGroupByPriority(overdue) });
	}
	if (dueThisWeek.length > 0) {
		sections.push({
			section: 'Due this week',
			items: sortGroupByPriority(dueThisWeek),
		});
	}
	if (upcoming.length > 0) {
		sections.push({
			section: 'Upcoming',
			items: sortGroupByPriority(upcoming),
		});
	}
	if (noDueDate.length > 0) {
		sections.push({
			section: 'No Due Date',
			items: sortGroupByPriority(noDueDate),
		});
	}
	return sections;
};

// Filter tasks created today, sorted by priority
export const filterTasksCreatedToday = (tasks) => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const tomorrow = new Date(today);
	tomorrow.setDate(tomorrow.getDate() + 1);

	const todayTasks = tasks.filter((task) => {
		if (!task.created_date) return false;
		const createdDate = new Date(task.created_date);
		// Use range comparison because Date objects can't be compared with ==
		// This ensures we only get tasks from today (midnight to midnight)
		return createdDate >= today && createdDate < tomorrow;
	});

	// Sort by priority
	const sortedTasks = todayTasks.sort((a, b) => {
		return getPriorityWeight(a.priority) - getPriorityWeight(b.priority);
	});

	// Return as a single section
	return [{ section: "What's new", items: sortedTasks }];
};

// Count overdue tasks 
export const countOverdueTasks = (tasks) => {
	if (!Array.isArray(tasks)) return 0;

	const today = new Date();
	today.setHours(0,0,0,0);

	return tasks.filter(
		(t) => t.due_date && new Date(t.due_date + 'T00:00:00') < today
	).length;
};
